import React, { useState, useEffect } from 'react';
import WeekPicker from './WeekPicker';
import { format, addWeeks, startOfWeek, endOfWeek } from 'date-fns';
import PMDashboardService from '../services/PMDashboardService';
import { ProjectDataService } from '../services/ProjectDataService';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

// CollapsibleProject component for individual project display
const CollapsibleProject = ({ project, formatNumber, formatCurrency, formatPercent }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="pm-group">
      <div 
        className="collapsible-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
        <h3>{project.name} - MS {project.projectNumber ? project.projectNumber.split('-').pop() || project.projectNumber : 'N/A'
  }</h3>
        <div className="project-info">
          <span>Contract Labor: {formatCurrency(project.labor)}</span>
          <span>Total Hours: {formatNumber(project.totalHours)}</span>
          <span>Projected Cost: {formatCurrency(project.totalCost)}</span>
          <span>% EAC Labor Used: {formatPercent(project.laborUsed)}</span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="collapsible-content pm-projects">
          <table className="summary-table resource-details">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Labor Grade</th>
                <th>Planned Hours</th>
              </tr>
            </thead>
            <tbody>
              {project.resources && project.resources.map((resource, index) => (
                <tr key={index}>
                  <td>{resource.name}</td>
                  <td>{resource.laborCategory}</td>
                  <td className="number-cell">{formatNumber(resource.hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main PM Page Component
const PMPage = ({ navigate }) => {
  const [dashboardData, setDashboardData] = useState({ projects: [], summary: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekStartDate, setWeekStartDate] = useState(null);
  const [weekEndDate, setWeekEndDate] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  // New filter state
  const [filters, setFilters] = useState({
    pm: '',
    name: '',
    projectNumber: ''
  });
  // For expand/collapse state per group
  const [expandedGroups, setExpandedGroups] = useState({});

  // Format functions
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    // Add debugging
    console.log("Formatting percentage value:", value, "Type:", typeof value);
    
    // Convert to number and handle invalid values
    const numValue = parseFloat(value) || 0;
    console.log("Parsed percentage value:", numValue);
    
    // Case 1: Values like 9639 (should be 96.39%)
    if (numValue > 100 && numValue % 1 === 0) {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numValue / 10000);
    }
    
    else if (numValue >= 1 && numValue <= 100) {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numValue / 100);
    }
    // Case 3: Values like 0.78 (already in decimal form, should be 78.00%)
    else if (numValue > 0 && numValue < 1) {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numValue);
    }
    // Default case: just format as percentage with 2 decimal places
    else {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numValue / 100);
    }
};
  // const formatPercent = (value) => {
  //   // Convert to number and handle invalid values
  //   const numValue = parseFloat(value) || 0;
    
  //   // Check if the value is already scaled to represent percentage directly
  //   if (numValue >= 100 && numValue % 100 === 0) {
  //     return new Intl.NumberFormat('en-US', {
  //       style: 'percent',
  //       minimumFractionDigits: 1,
  //       maximumFractionDigits: 1,
  //     }).format(numValue / 10000);
  //   } else {
  //     return new Intl.NumberFormat('en-US', {
  //       style: 'percent',
  //       minimumFractionDigits: 1,
  //       maximumFractionDigits: 1,
  //     }).format(numValue / 100);
  //   }
  // };

  // Load project managers on component mount
  useEffect(() => {
    // Initialize with stored week date or default to next week
    try {
      const storedDate = localStorage.getItem('selectedWeekDate');
      let initialDate;
      
      if (storedDate) {
        initialDate = new Date(storedDate);
        if (!(initialDate instanceof Date) || isNaN(initialDate)) {
          // Invalid date from storage, use default
          initialDate = addWeeks(new Date(), 1);
        }
      } else {
        // No stored date, use default
        initialDate = addWeeks(new Date(), 1);
      }
      
      const startDate = startOfWeek(initialDate, { weekStartsOn: 1 });
      const endDate = endOfWeek(initialDate, { weekStartsOn: 1 });
      
      console.log("PM Page - Initializing with dates:", {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      });
      
      setWeekStartDate(startDate);
      setWeekEndDate(endDate);
    } catch (e) {
      console.warn("Error getting dates from localStorage:", e);
      // Fall back to next week if there's an error
      const today = addWeeks(new Date(), 1);
      const startDate = startOfWeek(today, { weekStartsOn: 1 });
      const endDate = endOfWeek(today, { weekStartsOn: 1 });
      setWeekStartDate(startDate);
      setWeekEndDate(endDate);
    }
  }, []);

  // Debug data state changes
  useEffect(() => {
    console.log("Dashboard data in state:", dashboardData);
    console.log(`Project count in state: ${dashboardData.projects ? dashboardData.projects.length : 0}`);
  }, [dashboardData]);

  // Handle week change
  const handleWeekChange = (startDate, endDate) => {
    console.log("PM dashboard week changed:", {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    });
    
    // Clear any cached data to ensure fresh load
    try {
      ProjectDataService.clearCacheWithPattern('pm_dashboard_');
    } catch (e) {
      console.warn("Failed to clear PM dashboard cache:", e);
    }
    
    setWeekStartDate(startDate);
    setWeekEndDate(endDate);
    setRetryCount(0); // Reset retry counter when dates change
  };
  
  // Handle retry button click
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    setRetryCount(retryCount + 1);
  };

  // Load dashboard data when week dates change
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!weekStartDate || !weekEndDate) return;
      
      setIsLoading(true);
      setError(null);
      
      console.log("Loading dashboard data with params:", {
        startDate: format(weekStartDate, 'yyyy-MM-dd'),
        endDate: format(weekEndDate, 'yyyy-MM-dd'),
      });
      
      try {
        const data = await PMDashboardService.getPMDashboardData(
          format(weekStartDate, 'yyyy-MM-dd'),
          format(weekEndDate, 'yyyy-MM-dd'),
        );
        
        console.log("Dashboard data received in component:", data);
        console.log(`Projects count: ${data.projects ? data.projects.length : 0}`);
        
        // Verify data structure before setting state
        if (!data || !data.projects) {
          console.error("Received invalid data structure:", data);
          setError('Data received from server has an unexpected format');
          setDashboardData({ projects: [], summary: {} });
        } else {
          // Force state update with a new object reference
          setDashboardData({...data});
          console.log("State updated with dashboard data");
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load project data. ' + (err.message || 'Please try again later.'));
        setDashboardData({ projects: [], summary: {} });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDashboardData();
  }, [weekStartDate, weekEndDate, retryCount]);

  // Update getFilteredProjectsByPM to use all three filters
  const getFilteredProjectsByPM = () => {
    if (!dashboardData.projects) return {};
    let filtered = dashboardData.projects.filter(project => {
      return (
        (project.pm || '').toLowerCase().includes(filters.pm.toLowerCase()) &&
        (project.name || '').toLowerCase().includes(filters.name.toLowerCase()) &&
        (project.projectNumber || '').toLowerCase().includes(filters.projectNumber.toLowerCase())
      );
    });
    // Group by PM
    const grouped = {};
    filtered.forEach(project => {
      const pm = project.pm || 'Unassigned';
      if (!grouped[pm]) grouped[pm] = [];
      grouped[pm].push(project);
    });
    return grouped;
  };

  return (
    <main className="main-content">
      <div className="content-wrapper">
        <div className="table-container">
          <WeekPicker onWeekChange={handleWeekChange} />
          {/* Filter UI */}
          <div className="filter-bar-container" style={{ margin: '16px 0 24px 0', padding: '12px', background: '#f3f4f4', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
            <div className="filter-container">
              <div className="filter-group" style={{ width: '100%', display: 'flex', gap: '16px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Filter by PM"
                  value={filters.pm}
                  onChange={e => setFilters({ ...filters, pm: e.target.value })}
                  className="filter-input"
                />
                <input
                  type="text"
                  placeholder="Filter by Project Name"
                  value={filters.name}
                  onChange={e => setFilters({ ...filters, name: e.target.value })}
                  className="filter-input"
                />
                <input
                  type="text"
                  placeholder="Filter by Project Number"
                  value={filters.projectNumber}
                  onChange={e => setFilters({ ...filters, projectNumber: e.target.value })}
                  className="filter-input"
                />
                <button
                  className="clear-filters-btn"
                  onClick={() => setFilters({ pm: '', name: '', projectNumber: '' })}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
          <div className="pm-dashboard">
            <div className='pm-dashboard-title'>Project Planning Summary</div>
            {error && (
              <div className="error-banner">
                <p>{error}</p>
                <button className="retry-button" onClick={handleRetry}>
                  Retry
                </button>
              </div>
            )}
            {isLoading ? (
              <div className="loading">Loading summary data...</div>
            ) : (() => {
              const grouped = getFilteredProjectsByPM();
              const pmKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
              if (pmKeys.length === 0) {
                return (
                  <div className="no-data">
                    No projects found for the selected filter and date range.
                  </div>
                );
              }
              return (
                <div className="pm-groups">
                  {pmKeys.map(pm => {
                    const isExpanded = expandedGroups[pm] || false;
                    const projects = grouped[pm];
                    return (
                      <div className="pm-group" key={pm}>
                        <div
                          className="collapsible-header"
                          onClick={() => setExpandedGroups(prev => ({ ...prev, [pm]: !isExpanded }))
                          }
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          {isExpanded ? <FaChevronDown style={{ marginRight: 8 }} /> : <FaChevronRight style={{ marginRight: 8 }} />}
                          <h3 style={{ margin: 0 }}>{pm}</h3>
                          <span style={{ marginLeft: 12, color: '#888' }}>
                            ({projects.length} project{projects.length > 1 ? 's' : ''})
                          </span>
                        </div>
                        {isExpanded && (
                          <div className="collapsible-content pm-projects">
                            {projects.map(project => (
                              <CollapsibleProject
                                key={project.projectNumber}
                                project={project}
                                formatNumber={formatNumber}
                                formatCurrency={formatCurrency}
                                formatPercent={formatPercent}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </main>
  );
};

export default PMPage;